import React from "react";
import { motion } from "framer-motion";

/**
 * Design-partner testimonial slot.
 *
 * No fabricated quote — the brief explicitly forbids fake "trusted by" social
 * proof. This component renders a clearly-labelled "Design partner" frame
 * with placeholder copy and instructions. As soon as a real shadow-mode quote
 * comes in, swap the strings below.
 */
export default function DesignPartnerQuote({
  quote,
  attribution,
  detail,
}) {
  const isPlaceholder = !quote;

  return (
    <section
      id="design-partner"
      data-testid="design-partner-section"
      className="bg-[#FAF7F2] py-24 sm:py-28"
    >
      <div className="max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-3">
            <span
              className={`font-mono-spec text-[11px] tracking-[0.22em] uppercase font-bold px-2 py-1 ${
                isPlaceholder
                  ? "bg-[#FBF1E4] text-[#9B6B2F] border border-[#E5C8A4]"
                  : "bg-[#0B3D5C] text-[#E07B3A]"
              }`}
            >
              {isPlaceholder ? "Design partner · slot reserved" : "Design partner"}
            </span>
            <span className="hairline flex-1" />
          </div>

          <blockquote
            className="mt-8 font-serif-body text-[clamp(1.6rem,3.4vw,2.6rem)] leading-tight tracking-tight text-[#1A1A1A]"
            data-testid="design-partner-quote"
          >
            {isPlaceholder ? (
              <>
                &ldquo;A real quote from our first shadow-mode roofer lands
                here the day we close them. We&rsquo;d rather leave the space
                honest than fake it.&rdquo;
              </>
            ) : (
              <>&ldquo;{quote}&rdquo;</>
            )}
          </blockquote>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="font-sans-tight font-bold text-[#1A1A1A] text-[15px]">
                {isPlaceholder ? "Reserved for a real founding customer" : attribution}
              </p>
              <p className="font-serif-body text-[#4A4A4A] text-[14px] mt-1">
                {isPlaceholder
                  ? "We don't run stock-photo testimonials. The first signed quote replaces this card."
                  : detail}
              </p>
            </div>
            {isPlaceholder && (
              <a
                href="#founding"
                className="btn btn-ghost text-[14px]"
                data-testid="design-partner-cta"
              >
                Be the first quote on this page
              </a>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
